import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { X } from "lucide-react";
import { Button } from "../ui/button";
import { GpxSection } from "./GpxSection";
import { MapSection } from "./MapSection";
import { parseGPXFile } from "../../utils/gpxParser";
import { parseTCXFile, summarizeTCXSamples } from "../../utils/tcxParser";
import {
  calculateDistance,
  calculateDuration,
  calculateCenter,
} from "../../utils/gpx";

// Data interface for getting/setting form data
export interface HikingFormData {
  name: string;
  description: string;
  difficulty: "Easy" | "Moderate" | "Hard" | "Expert";
  elevationGain: number;
  avgSpeed?: number;
  maxSpeed?: number;
  avgPace?: number;
  maxPace?: number;
  avgHeartRate?: number;
  maxHeartRate?: number;
  selectedPhotos: File[];
  existingPhotos: string[];
  userGpxData: any;
  dogGpxData: any;
  locationCoordinate: [number, number];
}

// Handle interface for parent component
export interface HikingFormHandle {
  getData: () => HikingFormData;
  setData: (data: Partial<HikingFormData>) => void;
}

interface HikingFormProps {
  location: string;
  setLocation: (location: string) => void;
  onDistanceChange: (distance: number) => void;
  onDurationChange: (duration: number) => void;
  onLocationCoordinateChange: (coords: [number, number]) => void;
  onDateChange?: (date: string) => void;
}

export const HikingForm = forwardRef<HikingFormHandle, HikingFormProps>(
  (
    {
      location,
      setLocation,
      onDistanceChange,
      onDurationChange,
      onLocationCoordinateChange,
      onDateChange,
    },
    ref,
  ) => {
    // Internal state - all hiking-specific fields
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [difficulty, setDifficulty] = useState<
      "Easy" | "Moderate" | "Hard" | "Expert"
    >("Moderate");
    const [elevationGain, setElevationGain] = useState(0);
    const [avgSpeed, setAvgSpeed] = useState<number | undefined>();
    const [maxSpeed, setMaxSpeed] = useState<number | undefined>();
    const [avgPace, setAvgPace] = useState<number | undefined>();
    const [maxPace, setMaxPace] = useState<number | undefined>();
    const [avgHeartRate, setAvgHeartRate] = useState<number | undefined>();
    const [maxHeartRate, setMaxHeartRate] = useState<number | undefined>();
    const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
    const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
    const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);

    // GPX state (now internal to hiking)
    const [dogGpxFile, setDogGpxFile] = useState<File | null>(null);
    const [userGpxFile, setUserGpxFile] = useState<File | null>(null);
    const [dogGpxData, setDogGpxData] = useState<any>(null);
    const [userGpxData, setUserGpxData] = useState<any>(null);
    const [gpxError, setGpxError] = useState("");
    const [previewDogPath, setPreviewDogPath] = useState<
      [number, number][] | null
    >(null);
    const [previewUserPath, setPreviewUserPath] = useState<
      [number, number][] | null
    >(null);
    const [locationCoordinate, setLocationCoordinate] = useState<
      [number, number]
    >([46.2563, 5.6554]);

    // Generate photo previews when selectedPhotos changes
    useEffect(() => {
      const newPhotoPreviews = selectedPhotos.map((file) =>
        URL.createObjectURL(file),
      );
      setPhotoPreviews(newPhotoPreviews);

      return () => {
        newPhotoPreviews.forEach((url) => URL.revokeObjectURL(url));
      };
    }, [selectedPhotos]);

    // GPX Upload Handler
    const handleGPXUpload =
      (type: "dog" | "user") =>
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const extension = file.name.toLowerCase().split(".").pop();
        if (type === "dog" ? extension !== "gpx" : extension !== "gpx" && extension !== "tcx") {
          setGpxError(type === "dog" ? "Veuillez sélectionner un fichier GPX valide" : "Veuillez sélectionner un fichier GPX ou TCX valide");
          return;
        }

        try {
          const content = await file.text();
          const gpxData = extension === "tcx" ? parseTCXFile(content) : parseGPXFile(content);

          if (!gpxData) {
            setGpxError(`Erreur lors de l'analyse du fichier ${extension?.toUpperCase()}`);
            return;
          }

          if (gpxData.center) {
            setLocationCoordinate(gpxData.center as [number, number]);
            onLocationCoordinateChange(gpxData.center as [number, number]);
          }

          if (type === "user") {
            setUserGpxFile(file);
            setUserGpxData(gpxData);
            onDistanceChange(gpxData.distance);
            if (gpxData.elevationGain) {
              setElevationGain(gpxData.elevationGain);
            }
            if (gpxData.date) onDateChange?.(gpxData.date.slice(0, 10));
            setAvgSpeed(gpxData.avgSpeed);
            setMaxSpeed(gpxData.maxSpeed);
            setAvgPace(gpxData.avgPace);
            setMaxPace(gpxData.maxPace);
            setAvgHeartRate(gpxData.avgHeartRate);
            setMaxHeartRate(gpxData.maxHeartRate);
          } else {
            setDogGpxFile(file);
            setDogGpxData(gpxData);
          }

          if (gpxData.duration) {
            onDurationChange(gpxData.duration);
          }

          setGpxError("");
        } catch (error) {
          setGpxError(`Erreur lors de la lecture du fichier ${extension?.toUpperCase()}`);
          console.error(error);
        }
      };

    const handleRemoveGPX = (type: "dog" | "user") => () => {
      if (type === "dog") {
        setDogGpxFile(null);
        setDogGpxData(null);
        setPreviewDogPath(null);
      } else {
        setUserGpxFile(null);
        setUserGpxData(null);
        setPreviewUserPath(null);
      }
      setGpxError("");
    };

    const handlePreviewDogPath = (
      previewPath: [number, number][],
      newTimestamps?: (string | Date)[],
    ) => {
      setPreviewDogPath(previewPath);
    };

    const handleUpdateDogPath = (
      newPath: [number, number][],
      newTimestamps?: (string | Date)[],
    ) => {
      if (!dogGpxData) return;

      const newDistance = calculateDistance(newPath);
      const center = calculateCenter(newPath);

      setDogGpxData({
        ...dogGpxData,
        path: newPath,
        distance: newDistance,
        center,
        timestamps: newTimestamps,
      });

      setPreviewDogPath(null);
    };

    const handlePreviewUserPath = (
      previewPath: [number, number][],
      newTimestamps?: (string | Date)[],
    ) => {
      setPreviewUserPath(previewPath);
    };

    const handleUpdateUserPath = (
      newPath: [number, number][],
      newTimestamps?: (string | Date)[],
      range?: { startIndex: number; endIndex: number },
    ) => {
      if (!userGpxData) return;

      const newDistance = calculateDistance(newPath);
      const center = calculateCenter(newPath);

      const samples = userGpxData.samples && range
        ? userGpxData.samples.slice(range.startIndex, range.endIndex + 1)
        : userGpxData.samples;
      const metrics = samples ? summarizeTCXSamples(samples) : undefined;
      setUserGpxData({
        ...userGpxData,
        ...(metrics || {}),
        path: newPath,
        distance: metrics?.distance ?? newDistance,
        center,
        timestamps: newTimestamps,
        ...(samples ? { samples } : {}),
      });

      const nextDistance = metrics?.distance ?? newDistance;
      const nextDuration = metrics?.duration ?? calculateDuration(newTimestamps);
      onDistanceChange(nextDistance);
      onDurationChange(nextDuration);
      if (metrics) {
        setElevationGain(metrics.elevationGain);
        setAvgSpeed(metrics.avgSpeed);
        setMaxSpeed(metrics.maxSpeed);
        setAvgPace(metrics.avgPace);
        setMaxPace(metrics.maxPace);
        setAvgHeartRate(metrics.avgHeartRate);
        setMaxHeartRate(metrics.maxHeartRate);
      }
      setPreviewUserPath(null);
    };

    const handleLocationCoordinateChange = (coords: [number, number]) => {
      setLocationCoordinate(coords);
      onLocationCoordinateChange(coords);
    };

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      getData: (): HikingFormData => ({
        name,
        description,
        difficulty,
        elevationGain,
        avgSpeed,
        maxSpeed,
        avgPace,
        maxPace,
        avgHeartRate,
        maxHeartRate,
        selectedPhotos,
        existingPhotos,
        userGpxData,
        dogGpxData,
        locationCoordinate,
      }),
      setData: (data: Partial<HikingFormData>) => {
        if (data.name !== undefined) setName(data.name);
        if (data.description !== undefined) setDescription(data.description);
        if (data.difficulty !== undefined) setDifficulty(data.difficulty);
        if (data.elevationGain !== undefined)
          setElevationGain(data.elevationGain);
        if ("avgSpeed" in data) setAvgSpeed(data.avgSpeed);
        if ("maxSpeed" in data) setMaxSpeed(data.maxSpeed);
        if ("avgPace" in data) setAvgPace(data.avgPace);
        if ("maxPace" in data) setMaxPace(data.maxPace);
        if ("avgHeartRate" in data) setAvgHeartRate(data.avgHeartRate);
        if ("maxHeartRate" in data) setMaxHeartRate(data.maxHeartRate);
        if (data.selectedPhotos !== undefined)
          setSelectedPhotos(data.selectedPhotos);
        if (data.existingPhotos !== undefined)
          setExistingPhotos(data.existingPhotos);
        if (data.userGpxData !== undefined) setUserGpxData(data.userGpxData);
        if (data.dogGpxData !== undefined) setDogGpxData(data.dogGpxData);
        if (data.locationCoordinate !== undefined)
          setLocationCoordinate(data.locationCoordinate);
      },
    }));

    return (
      <>
        <div className="space-y-2">
          <Label htmlFor="name">Nom de la randonnée *</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: Mont Blanc"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="description">Observations</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ajoutez vos observations..."
            rows={3}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="difficulty">Difficulté</Label>
            <Select
              value={difficulty}
              onValueChange={(v) =>
                setDifficulty(v as "Easy" | "Moderate" | "Hard" | "Expert")
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Easy">Facile</SelectItem>
                <SelectItem value="Moderate">Modérée</SelectItem>
                <SelectItem value="Hard">Difficile</SelectItem>
                <SelectItem value="Expert">Expert</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="elevationGain">Dénivelé positif (m)</Label>
            <Input
              id="elevationGain"
              type="number"
              value={elevationGain}
              onChange={(e) => setElevationGain(Number(e.target.value))}
              placeholder="0"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hike-avg-speed">Vitesse moyenne (km/h)</Label>
            <Input id="hike-avg-speed" type="number" min="0" step="0.1" value={avgSpeed ?? ""} onChange={(event) => setAvgSpeed(event.target.value === "" ? undefined : Number(event.target.value))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hike-max-speed">Vitesse maximale (km/h)</Label>
            <Input id="hike-max-speed" type="number" min="0" step="0.1" value={maxSpeed ?? ""} onChange={(event) => setMaxSpeed(event.target.value === "" ? undefined : Number(event.target.value))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hike-avg-pace">Allure moyenne (min/km)</Label>
            <Input id="hike-avg-pace" type="number" min="0" step="0.1" value={avgPace ?? ""} onChange={(event) => setAvgPace(event.target.value === "" ? undefined : Number(event.target.value))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hike-max-pace">Allure maximale (meilleure, min/km)</Label>
            <Input id="hike-max-pace" type="number" min="0" step="0.1" value={maxPace ?? ""} onChange={(event) => setMaxPace(event.target.value === "" ? undefined : Number(event.target.value))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hike-avg-heart-rate">Fréquence cardiaque moyenne (bpm)</Label>
            <Input id="hike-avg-heart-rate" type="number" min="0" value={avgHeartRate ?? ""} onChange={(event) => setAvgHeartRate(event.target.value === "" ? undefined : Number(event.target.value))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hike-max-heart-rate">Fréquence cardiaque maximale (bpm)</Label>
            <Input id="hike-max-heart-rate" type="number" min="0" value={maxHeartRate ?? ""} onChange={(event) => setMaxHeartRate(event.target.value === "" ? undefined : Number(event.target.value))} />
          </div>
        </div>

        {/* GPX Section - now part of HikingForm */}
        <GpxSection
          category="hiking"
          dogGpxFile={dogGpxFile}
          userGpxFile={userGpxFile}
          dogGpxData={dogGpxData}
          userGpxData={userGpxData}
          gpxError={gpxError}
          handleGPXUpload={handleGPXUpload}
          handleRemoveGPX={handleRemoveGPX}
          handleUpdateDogPath={handleUpdateDogPath}
          handlePreviewDogPath={handlePreviewDogPath}
          handleUpdateUserPath={handleUpdateUserPath}
          handlePreviewUserPath={handlePreviewUserPath}
        />

        {/* Map Section - now part of HikingForm */}
        <MapSection
          category="hiking"
          dogGpxData={dogGpxData}
          userGpxData={userGpxData}
          locationCoordinate={locationCoordinate}
          previewDogPath={previewDogPath}
          previewUserPath={previewUserPath}
          setLocation={setLocation}
          setLocationCoordinate={handleLocationCoordinateChange}
          location={location}
        />

        <div className="space-y-4">
          <h3 className="text-lg text-blue-900">Photos de la randonnée</h3>
          <div className="space-y-2">
            <Label htmlFor="photos">Ajouter des photos</Label>
            <Input
              id="photos"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                if (e.target.files) {
                  setSelectedPhotos(Array.from(e.target.files));
                }
              }}
            />
          </div>

          {/* Display selected new photos */}
          {selectedPhotos.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
              {photoPreviews.map((photoUrl, index) => (
                <div key={photoUrl + index} className="relative">
                  <img
                    src={photoUrl}
                    alt={`Preview ${index}`}
                    className="w-full h-32 object-cover rounded-md"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 h-6 w-6"
                    onClick={() =>
                      setSelectedPhotos(
                        selectedPhotos.filter((_, i) => i !== index),
                      )
                    }
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Display existing photos */}
          {existingPhotos.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
              {existingPhotos.map((photoUrl, index) => (
                <div key={photoUrl + index} className="relative">
                  <img
                    src={photoUrl}
                    alt={`Existing ${index}`}
                    className="w-full h-32 object-cover rounded-md"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 h-6 w-6"
                    onClick={() =>
                      setExistingPhotos(
                        existingPhotos.filter((_, i) => i !== index),
                      )
                    }
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    );
  },
);

HikingForm.displayName = "HikingForm";
